import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { catchError, map } from 'rxjs/operators';
import { MediaType } from "src/app/models/constants/media-type";
import { ContractCancel } from "src/app/models/contract/contract-cancel";
import { ContractCancelInput } from "src/app/models/contract/contract-cancel-input";
import { ContractEdit } from "src/app/models/contract/contract-edit";
import { ContractEditCancel } from "src/app/models/contract/contract-edit-cancel";
import { ContractRevertRequest } from "src/app/models/contract/contract-revert-request";
import { ContractSignature } from "src/app/models/contract/contract-signature";
import { ContractVinCancelRequest } from "src/app/models/contract/contract-vin-cancel-request";
import { CreateContractSignature } from "src/app/models/contract/create-contract-signature";
import { DownloadFile } from "src/app/models/contract/download-file";
import { ExportContractFromExcelBusinessBra } from "src/app/models/contract/export-contract-from-excel-business-bra";
import { ExportContractFromExcelDealer } from "src/app/models/contract/export-contract-from-excel-dealer";
import { FilterContractSearch } from "src/app/models/contract/filter-contract-search";
import { FilterContractSearchResult } from "src/app/models/contract/filter-contract-search-result";
import { FilterContractSearchResultSimplified } from "src/app/models/contract/filter-contract-search-result-simplified";
import { FilterContractSearchResultTotalRecord } from "src/app/models/contract/filter-contract-search-result-total-record";
import { SalesTypeFord } from "src/app/models/contract/sales-type-ford";
import { Term } from "src/app/models/contract/term";
import { TermGeneration } from "src/app/models/contract/term-generation";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: 'root'
})
export class ContractService {

  private readonly BUSINESS_CONTEXT = 'business';
  private readonly DEALER_CONTEXT = 'dealer';

  private apiUrl = environment.apiUrl;
  private filterContractSearchResultSimplifiedBusiness!: FilterContractSearchResultSimplified;
  private filterContractSearchResultSimplifiedDealer!: FilterContractSearchResultSimplified;
  private filterContractSearchBusiness!: FilterContractSearch;
  private filterContractSearchDealer!: FilterContractSearch;
  private restoreContractSearchOnReturn = false;

  constructor(private httpClient: HttpClient) {
  }

  setFilterContractSearchResultSimplified(filterContractSearchResultSimplified: FilterContractSearchResultSimplified) {
    this.setFilterContractSearchResultSimplifiedByContext(this.BUSINESS_CONTEXT, filterContractSearchResultSimplified);
  }

  getFilterContractSearchResultSimplified(): FilterContractSearchResultSimplified {
    return this.getFilterContractSearchResultSimplifiedByContext(this.BUSINESS_CONTEXT);
  }

  setFilterContractSearch(filterContractSearch: FilterContractSearch) {
    this.setFilterContractSearchByContext(this.BUSINESS_CONTEXT, filterContractSearch);
  }

  getFilterContractSearch(): FilterContractSearch {
    return this.getFilterContractSearchByContext(this.BUSINESS_CONTEXT);
  }

  setFilterContractSearchByContext(context: 'business' | 'dealer', filterContractSearch: FilterContractSearch) {
    const clonedFilter = Object.assign(new FilterContractSearch(), filterContractSearch);

    if (context === this.DEALER_CONTEXT) {
      this.filterContractSearchDealer = clonedFilter;
      return;
    }

    this.filterContractSearchBusiness = clonedFilter;
  }

  getFilterContractSearchByContext(context: 'business' | 'dealer'): FilterContractSearch {
    return context === this.DEALER_CONTEXT
      ? this.filterContractSearchDealer
      : this.filterContractSearchBusiness;
  }

  setFilterContractSearchResultSimplifiedByContext(context: 'business' | 'dealer', filterContractSearchResultSimplified: FilterContractSearchResultSimplified) {
    const clonedResult = Object.assign(new FilterContractSearchResultSimplified(), filterContractSearchResultSimplified);

    if (context === this.DEALER_CONTEXT) {
      this.filterContractSearchResultSimplifiedDealer = clonedResult;
      return;
    }

    this.filterContractSearchResultSimplifiedBusiness = clonedResult;
  }

  getFilterContractSearchResultSimplifiedByContext(context: 'business' | 'dealer'): FilterContractSearchResultSimplified {
    return context === this.DEALER_CONTEXT
      ? this.filterContractSearchResultSimplifiedDealer
      : this.filterContractSearchResultSimplifiedBusiness;
  }

  clearFilterContractSearchByContext(context: 'business' | 'dealer') {
    if (context === this.DEALER_CONTEXT) {
      this.filterContractSearchDealer = undefined as unknown as FilterContractSearch;
      this.filterContractSearchResultSimplifiedDealer = undefined as unknown as FilterContractSearchResultSimplified;
      return;
    }

    this.filterContractSearchBusiness = undefined as unknown as FilterContractSearch;
    this.filterContractSearchResultSimplifiedBusiness = undefined as unknown as FilterContractSearchResultSimplified;
  }

  setRestoreContractSearchOnReturn(shouldRestore: boolean): void {
    this.restoreContractSearchOnReturn = shouldRestore;
  }

  consumeRestoreContractSearchOnReturn(): boolean {
    const shouldRestore = this.restoreContractSearchOnReturn;
    this.restoreContractSearchOnReturn = false;
    return shouldRestore;
  }

  contractSearch(filterContractSearch: FilterContractSearch): Observable<FilterContractSearchResultTotalRecord> {
    return this.httpClient.post<FilterContractSearchResultTotalRecord>(`${this.apiUrl}/api/contracts/search`, filterContractSearch);
  }

  dealerContractSearch(filterContractSearch: FilterContractSearch): Observable<FilterContractSearchResultTotalRecord> {
    return this.httpClient.post<FilterContractSearchResultTotalRecord>(`${this.apiUrl}/api/contracts/dealer/search`, filterContractSearch);
  }

  contractSearchExcel(filterContractSearch: FilterContractSearch): Observable<ExportContractFromExcelBusinessBra[]> {
    return this.httpClient.post<ExportContractFromExcelBusinessBra[]>(`${this.apiUrl}/api/contracts/search/excel`, filterContractSearch);
  }

  dealerContractSearchExcel(filterContractSearch: FilterContractSearch): Observable<ExportContractFromExcelDealer[]> {
    return this.httpClient.post<ExportContractFromExcelDealer[]>(`${this.apiUrl}/api/contracts/dealer/search/excel`, filterContractSearch);
  }

  contractEditPost(contractEdit: ContractEdit): Observable<boolean> {
    return this.httpClient.post<boolean>(`${this.apiUrl}/api/contracts/edit`, contractEdit);
  }

  searchSalesTypeFord(): Observable<SalesTypeFord[]> {
    return this.httpClient.get<SalesTypeFord[]>(`${this.apiUrl}/api/contracts/search/sales/type`);
  }

  searchContractId(contractId: number, productKey: number, coverageCategoryKey: number): Observable<FilterContractSearchResult> {
    return this.httpClient.get<FilterContractSearchResult>(`${this.apiUrl}/api/contracts/search/${contractId}/${productKey}/${coverageCategoryKey}`);
  }

  getContractVinCancel(contractId: number, vin: string): Observable<ContractCancel> {

    const request = new ContractVinCancelRequest();
    request.contractId = contractId;
    request.vin = vin;

    return this.httpClient.post<ContractCancel>(`${this.apiUrl}/api/v2/contract/vin/cancel`, request);
  }

  updateContractCancel(editedContractCancel: ContractEditCancel): Observable<ContractCancel> {
    return this.httpClient.post<ContractCancel>(`${this.apiUrl}/api/v1/contract/vin/cancel/edit`, editedContractCancel);
  }

  downloadFileSignatureBase64(contractId: number): Observable<DownloadFile> {
    return this.httpClient.get<DownloadFile>(`${this.apiUrl}/api/contracts/download/${contractId}`);
  }

  downloadFile(contractId: number): Observable<DownloadFile> {
    return this.httpClient.get<DownloadFile>(`${this.apiUrl}/api/file/term/${contractId}`);
  }

  createFileTerm(termGeneration: TermGeneration): Observable<Blob> {

    const headers = new HttpHeaders({ 'Content-Type': MediaType.APPLICATION_JSON });
    return this.httpClient.post(`${this.apiUrl}/api/file/term`, termGeneration, {
      headers,
      responseType: 'blob'
    });
  }

  getPdfFile(contractId: number, ticketFileId: number): Observable<Blob> {

    const params = new HttpParams()
      .set('contractId', contractId.toString())
      .set('fileId', ticketFileId.toString());

    const headers = new HttpHeaders({
      Accept: MediaType.APPLICATION_OCTET_STREAM_VALUE,
    });

    return this.httpClient.get(`${this.apiUrl}/api/file/aig/pdf`, {
      headers,
      params,
      responseType: 'blob',
    });
  }

  searchByContractIdAndFileType(contractId: number, fileTypes: number[]): Observable<Term | null> {
    const fileTypesString = btoa(JSON.stringify(fileTypes));
    return this.httpClient.get<Term>(`${this.apiUrl}/api/file/term/search/${contractId}/${fileTypesString}`)
      .pipe(
        catchError((error) => {
          if (error?.status === 404) {
            return of(null);
          }
          return throwError(() => error);
        })
      );
  }

  sendPaymentLinkByEmail(contractId: number): Observable<boolean> {
    return this.httpClient.get<boolean>(`${this.apiUrl}/api/contracts/payment/link/${contractId}`);
  }

  public getContractSignature(uuid: string): Observable<ContractSignature> {
    return this.httpClient.get<ContractSignature>(`${this.apiUrl}/api/contracts/signature/${uuid}`)
      .pipe(
        map((response: ContractSignature) => {
          return response;
        })
      );
  }

  determineCancelValues(contractCancelInput: ContractCancelInput): Observable<ContractCancel> {
    return this.httpClient.post<ContractCancel>(`${this.apiUrl}/api/v2/cancel/values`, contractCancelInput);
  }

  cancelContract(contractCancel: ContractCancel): Observable<boolean> {
    return this.httpClient.post<boolean>(`${this.apiUrl}/api/v2/contracts/cancel`, contractCancel).pipe(
      catchError((error) => {

        if (error.status === 200) {
          return of(true);
        }
        return throwError(() => error);
      })
    );
  }

  save(createContractSignature: CreateContractSignature): Observable<CreateContractSignature> {
    return this.httpClient.post<CreateContractSignature>(`${this.apiUrl}/api/contracts/signature/save`, createContractSignature);
  }

  undoCancellation(contractId: number, vin: string, productId: number): Observable<void> {

    const request = new ContractRevertRequest();
    request.contractId = contractId;
    request.vin = vin;
    request.productId = productId;

    return this.httpClient.post<void>(`${this.apiUrl}/api/v2/contract/cancellation/revert`, request)
      .pipe(
        catchError((error) => {
          if (error.status === 200) {
            return of(undefined);
          }
          return throwError(() => error);
        })
      );
  }

}